import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { CATEGORY_LABEL, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const PriorityPill = ({ priority }) => (
  <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium", priority === "overdue" ? "status-overdue" : "status-upcoming")}>
    {priority === "overdue" ? "Forfalt" : "Nærmar seg"}
  </span>
);

export default function Deler() {
  const [data, setData] = useState({ aggregated: [], machines: [] });
  const [loading, setLoading] = useState(true);
  const [onlyDue, setOnlyDue] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/parts/order-list?only_due=${onlyDue}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [onlyDue]);

  const exportCsv = () => {
    const rows = [["Del", "Delenr", "Kategori", "Sum antal", "Eining", "Maskiner", "Prioritet"]];
    data.aggregated.forEach((a) => {
      rows.push([
        a.name,
        a.part_number || "",
        CATEGORY_LABEL[a.category] || a.category || "",
        a.total_quantity,
        a.unit || "",
        a.machines.map((m) => `${m.label} (${num(m.quantity)})`).join("; "),
        a.priority === "overdue" ? "Forfalt" : "Nærmar seg",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bestillingsliste.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Bestillingsliste eksportert.");
  };

  const items = data.aggregated || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex min-h-[44px] items-center gap-3 rounded-lg border bg-card px-3 py-2 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0" htmlFor="onlyDue">
          <Switch id="onlyDue" checked={onlyDue} onCheckedChange={setOnlyDue} data-testid="order-only-due-switch" />
          <Label htmlFor="onlyDue" className="text-sm text-foreground">Berre maskiner som er forfalne / nærmar seg</Label>
        </label>
        <Button variant="outline" onClick={exportCsv} disabled={!items.length} data-testid="order-list-export-button" className="h-11 sm:h-10">
          <Download /> Eksporter CSV
        </Button>
      </div>

      <Card className="overflow-hidden shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-bold">
            Bestillingsliste
            <span className="ml-2 text-sm font-normal text-muted-foreground" data-testid="order-list-summary">
              {data.machines?.length || 0} maskiner · {items.length} delar
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : items.length === 0 ? (
            <EmptyState className="m-4 border-0" icon={Package} title="Ingen deler å bestille"
              description={onlyDue ? "Ingen maskiner med forfallen/nærliggjande service har registrerte deler." : "Legg inn deler på maskinene for å byggje ei bestillingsliste."} />
          ) : (
            <>
              {/* Mobile cards */}
              <div className="divide-y md:hidden" data-testid="order-card-list">
                {items.map((a, i) => (
                  <div key={i} className="px-4 py-3" data-testid={`order-card-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold">{a.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {a.part_number ? `Delenr ${a.part_number} · ` : ""}{CATEGORY_LABEL[a.category] || a.category || "—"}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-heading text-lg font-bold tabular-nums leading-tight">{num(a.total_quantity)} <span className="text-xs font-medium text-muted-foreground">{a.unit || ""}</span></div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0 flex-1 text-xs text-muted-foreground">
                        {a.machines.map((mm) => `${mm.label} (${num(mm.quantity)})`).join(", ")}
                      </div>
                      <PriorityPill priority={a.priority} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <Table data-testid="order-list-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Del</TableHead>
                      <TableHead>Delenr</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right">Sum antal</TableHead>
                      <TableHead>Maskiner</TableHead>
                      <TableHead>Prioritet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((a, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{a.name}</TableCell>
                        <TableCell className="text-muted-foreground">{a.part_number || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{CATEGORY_LABEL[a.category] || a.category || "—"}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{num(a.total_quantity)} {a.unit || ""}</TableCell>
                        <TableCell className="max-w-[280px] text-xs text-muted-foreground">
                          {a.machines.map((mm) => `${mm.label} (${num(mm.quantity)})`).join(", ")}
                        </TableCell>
                        <TableCell><PriorityPill priority={a.priority} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
